// features/inputs/hooks/useInputBatches.ts
import { useQuery, useMutation } from '@tanstack/react-query';
import { queryClient } from '@/shared/services/queryClient';
import {
  inputBatchesApi,
  type CreateInputBatchRequest,
} from '../services/inputBatchesApi';

export const useInputBatches = (inputId: string) => {
  return useQuery({
    queryKey: ['input-batches', inputId],
    queryFn: () => inputBatchesApi.getBatchesByInput(inputId),
    enabled: !!inputId,
  });
};

export const useInputBatch = (id: string) => {
  return useQuery({
    queryKey: ['input-batches', id],
    queryFn: () => inputBatchesApi.getBatchById(id),
    enabled: !!id,
  });
};

export const useActiveBatches = () => {
  return useQuery({
    queryKey: ['input-batches', 'active'],
    queryFn: inputBatchesApi.getActiveBatches,
  });
};

export const useExpiringBatches = (days: number = 30) => {
  return useQuery({
    queryKey: ['input-batches', 'expiring', days],
    queryFn: () => inputBatchesApi.getExpiringBatches(days),
  });
};

export const useCreateInputBatch = () => {
  return useMutation({
    mutationFn: (data: CreateInputBatchRequest) =>
      inputBatchesApi.createBatch(data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['input-batches'] });
      queryClient.invalidateQueries({
        queryKey: ['input-batches', variables.inputId],
      });
      queryClient.invalidateQueries({ queryKey: ['inputs'] });
    },
  });
};

export const useUpdateInputBatch = () => {
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: Partial<CreateInputBatchRequest>;
    }) => inputBatchesApi.updateBatch(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['input-batches'] });
      queryClient.invalidateQueries({ queryKey: ['inputs'] });
    },
  });
};

export const useDeleteInputBatch = () => {
  return useMutation({
    mutationFn: (id: string) => inputBatchesApi.deleteBatch(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['input-batches'] });
      queryClient.invalidateQueries({ queryKey: ['inputs'] });
    },
  });
};